import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Square, Clock, User, Car, Wrench, Lock, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const MechanicScanPage = () => {
  const { woId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auth state
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [pin, setPin] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authEmployee, setAuthEmployee] = useState(null);
  const [loginError, setLoginError] = useState('');

  // Timer state
  const [activeEntries, setActiveEntries] = useState([]);
  const [history, setHistory] = useState([]);
  const [elapsed, setElapsed] = useState({});

  useEffect(() => { loadData(); }, [woId]);

  // Elapsed ticker
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const newElapsed = {};
      activeEntries.forEach(e => {
        const start = new Date(e.start_time);
        const diff = Math.floor((now - start) / 1000);
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        const s = diff % 60;
        newElapsed[e.id] = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      });
      setElapsed(newElapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [activeEntries]);

  const loadData = async () => {
    try {
      const res = await axios.get(`${API}/public/scan/${woId}`);
      setData(res.data);
      setActiveEntries(res.data.active_entries || []);
      setHistory(res.data.history || []);
    } catch (err) {
      setError('Fiche de travail introuvable');
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await axios.post(`${API}/public/scan/login`, {
        employee_id: selectedEmployee,
        pin_code: pin,
      });
      setAuthenticated(true);
      setAuthEmployee(res.data);
      toast.success(`Bienvenue ${res.data.employee_name}`);
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Erreur de connexion');
    }
  };

  const startWork = async () => {
    try {
      await axios.post(`${API}/work-time/start`, {
        work_order_id: woId,
        employee_id: authEmployee.employee_id,
      });
      toast.success('Timer demarre');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const stopWork = async () => {
    try {
      const res = await axios.post(`${API}/work-time/stop`, {
        work_order_id: woId,
        employee_id: authEmployee.employee_id,
      });
      toast.success(`Timer arrete (${res.data.duration_minutes} min)`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const myActiveEntry = authenticated && authEmployee
    ? activeEntries.find(e => e.employee_id === authEmployee.employee_id)
    : null;

  const totalMinutes = history.reduce((sum, e) => sum + (e.duration_minutes || 0), 0);
  const totalH = Math.floor(totalMinutes / 60);
  const totalM = totalMinutes % 60;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <p className="text-slate-400">Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="p-8 text-center max-w-md rounded-xl bg-slate-800 border-slate-700">
          <Wrench className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <h1 className="text-xl font-bold text-white mb-2">Erreur</h1>
          <p className="text-sm text-slate-400">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4">
        {/* Header */}
        <div className="text-center">
          <img src="/logo-garage.png" alt="Garage" className="h-10 mx-auto mb-2" />
          <p className="text-xs text-slate-500">{data?.garage?.name}</p>
        </div>

        {/* Work Order Info */}
        <Card className="p-4 rounded-xl bg-slate-800 border-slate-700" data-testid="scan-wo-info">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-slate-300"><User className="w-4 h-4 text-slate-500" /><span>{data?.client_name}</span></div>
            <div className="flex items-center gap-2 text-slate-300"><Car className="w-4 h-4 text-slate-500" /><span>{data?.vehicle_info}</span></div>
            <div className="flex items-center gap-2 text-slate-300">
              <Wrench className="w-4 h-4 text-slate-500" />
              <span>{data?.category?.toUpperCase()}{data?.intervention_type ? ` - ${data.intervention_type}` : ''}</span>
            </div>
          </div>
        </Card>

        {/* Login Form */}
        {!authenticated ? (
          <Card className="p-5 rounded-xl bg-slate-800 border-slate-700" data-testid="scan-login-form">
            <div className="text-center mb-4">
              <Lock className="w-8 h-8 mx-auto mb-2 text-slate-400" />
              <h2 className="text-lg font-bold text-white">Connexion mecanicien</h2>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Votre nom</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="scan-employee-select">
                    <SelectValue placeholder="Sélectionnez..." />
                  </SelectTrigger>
                  <SelectContent>
                    {data?.employees?.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">Code PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="****"
                  className="bg-slate-700 border-slate-600 text-white text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="scan-pin-input"
                />
              </div>
              {loginError && <p className="text-red-400 text-sm text-center">{loginError}</p>}
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base" disabled={!selectedEmployee || pin.length < 4} data-testid="scan-login-btn">
                <KeyRound className="mr-2 w-4 h-4" /> Se connecter
              </Button>
            </form>
          </Card>
        ) : (
          <>
            {/* Authenticated - Timer Controls */}
            <Card className="p-4 rounded-xl bg-slate-800 border-slate-700 text-center" data-testid="scan-auth-info">
              <p className="text-sm text-slate-400">Connecte en tant que</p>
              <p className="text-lg font-bold text-white">{authEmployee?.employee_name}</p>
            </Card>

            {myActiveEntry ? (
              /* Active Timer */
              <Card className="p-6 rounded-xl bg-emerald-900/50 border-emerald-700" data-testid="scan-active-timer">
                <div className="text-center space-y-4">
                  <span className="w-3 h-3 bg-emerald-400 rounded-full inline-block animate-pulse" />
                  <p className="text-sm text-emerald-300 font-medium">En cours depuis {myActiveEntry.start_time.substring(11, 16)}</p>
                  <p className="text-5xl font-mono font-bold text-white" data-testid="scan-timer-display">
                    {elapsed[myActiveEntry.id] || '00:00:00'}
                  </p>
                  <Button
                    onClick={stopWork}
                    className="w-full bg-red-600 hover:bg-red-700 h-14 text-lg"
                    data-testid="scan-stop-btn"
                  >
                    <Square className="mr-2 w-5 h-5" /> Arreter
                  </Button>
                </div>
              </Card>
            ) : (
              /* Start Button */
              <Button
                onClick={startWork}
                className="w-full bg-emerald-600 hover:bg-emerald-700 h-16 text-xl rounded-xl"
                data-testid="scan-start-btn"
              >
                <Play className="mr-2 w-6 h-6" /> Demarrer
              </Button>
            )}

            {/* Other Active Mechanics */}
            {activeEntries.filter(e => e.employee_id !== authEmployee?.employee_id).length > 0 && (
              <Card className="p-4 rounded-xl bg-slate-800 border-slate-700 space-y-2" data-testid="scan-other-mechanics">
                <p className="text-xs text-slate-400 font-bold uppercase">Aussi sur cette fiche</p>
                {activeEntries.filter(e => e.employee_id !== authEmployee?.employee_id).map(e => (
                  <div key={e.id} className="flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-slate-300">{e.employee_name}</span>
                    <span className="text-xs text-slate-500 ml-auto">depuis {e.start_time.substring(11, 16)}</span>
                  </div>
                ))}
              </Card>
            )}

            {/* History */}
            {history.length > 0 && (
              <Card className="p-4 rounded-xl bg-slate-800 border-slate-700 space-y-2" data-testid="scan-history">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400 font-bold uppercase flex items-center gap-1"><Clock className="w-3 h-3" /> Historique</p>
                  <span className="text-sm font-bold text-white">{totalH}h{String(totalM).padStart(2, '0')}</span>
                </div>
                {history.slice(0, 5).map(e => {
                  const h = Math.floor(e.duration_minutes / 60);
                  const m = e.duration_minutes % 60;
                  return (
                    <div key={e.id} className="flex items-center justify-between text-sm py-1">
                      <span className="text-slate-400">{e.employee_name}</span>
                      <span className="text-slate-300 font-mono">{h}h{String(m).padStart(2, '0')}</span>
                    </div>
                  );
                })}
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MechanicScanPage;
